import { Box, IconButton, Stack, useTheme } from "@mui/material";
import { Edit3, MessageCircle } from "lucide-react";
import { ChatListRow } from "web_myplann/Chat/Components/List/ChatListRow";
import { ChatListSearch } from "web_myplann/Chat/Components/List/ChatListSearch";
import { ChatListSkeleton } from "web_myplann/Chat/Components/List/ChatListSkeleton";
import { ChatRecipientPickerDrawer } from "web_myplann/Chat/Components/List/ChatRecipientPickerDrawer";
import { useChatListPage } from "web_myplann/Chat/Hooks/List/useChatListPage";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CenteredEmptyState } from "web_myplann/Shared/Components/CenteredEmptyState";
import { MyPlannNavigationHeader } from "web_myplann/Shared/Header/MyPlannNavigationHeader";
import useIntersectionObserver from "web_myplann/Shared/Hooks/useIntersectionObserver";

export const ChatListPage = () => {
	const theme = useTheme();
	const { t } = useResource();
	const {
		items,
		isLoading,
		hasMore,
		isLoadingMore,
		loadMore,
		query,
		setQuery,
		placeholder,
		isSearchable,
		openConversation,
		recipientsWithoutChat,
		canStartNewMessage,
		openNewMessage,
		pickerOpen,
		closePicker,
		startConversation,
	} = useChatListPage();

	const loadMoreRef = useIntersectionObserver(
		hasMore && !isLoadingMore ? loadMore : undefined,
		items.length,
		undefined,
		hasMore && !isLoadingMore,
	);

	return (
		<Stack height="100dvh" bgcolor={theme.palette.surface.muted}>
			<MyPlannNavigationHeader
				title={t(Resources.Chat.title)}
				defaultRoute={Routing.Dashboard}
				bgColor={theme.palette.background.paper}
				endComponent={
					canStartNewMessage ? (
						<IconButton
							variant="ghost"
							size="large"
							onClick={openNewMessage}
							aria-label={t(Resources.Chat.newMessage)}
						>
							<Edit3 size={theme.iconSize.md} color={theme.palette.grey[600]} />
						</IconButton>
					) : undefined
				}
			/>

			{isSearchable && <ChatListSearch value={query} onChange={setQuery} />}

			<Stack flex={1} minHeight={0} sx={{ overflowY: "auto" }}>
				{isLoading ? (
					<ChatListSkeleton />
				) : items.length === 0 ? (
					<Box px={theme.spacingTokens.lg} pt={theme.spacingTokens.xxl}>
						<CenteredEmptyState icon={MessageCircle} {...placeholder} />
					</Box>
				) : (
					<>
						<Box
							bgcolor={theme.palette.background.paper}
							mt={theme.spacingTokens.sm}
						>
							{items.map((conversation, index) => (
								<ChatListRow
									key={conversation.id}
									conversation={conversation}
									isLast={index === items.length - 1}
									onClick={() => openConversation(conversation)}
								/>
							))}
						</Box>
						<Box ref={loadMoreRef} height={1} />
						<Box height="env(safe-area-inset-bottom, 8px)" />
					</>
				)}
			</Stack>

			<ChatRecipientPickerDrawer
				open={pickerOpen}
				recipients={recipientsWithoutChat}
				onClose={closePicker}
				onSelect={startConversation}
			/>
		</Stack>
	);
};
